import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch,
  CircularProgress, Chip, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

interface SourceRow {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

const PatientSources: React.FC = () => {
  const [rows, setRows] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', sort_order: 0, is_active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/patient-sources'), { params: { includeInactive: true } });
      const data = res.data?.success !== false ? (res.data?.data ?? []) : [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', sort_order: (rows.reduce((m, r) => Math.max(m, r.sort_order || 0), 0)) + 10, is_active: true });
    setDlgOpen(true);
  };

  const openEdit = (r: SourceRow) => {
    setEditId(r.id);
    setForm({ name: r.name, sort_order: r.sort_order ?? 0, is_active: r.is_active !== false });
    setDlgOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(getApiUrl(`/clinic/patient-sources/${editId}`), form);
      } else {
        await axios.post(getApiUrl('/clinic/patient-sources'), form);
      }
      setDlgOpen(false);
      await load();
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.error || 'Erro ao salvar origem');
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (r: SourceRow) => {
    if (!window.confirm(`Desativar origem "${r.name}"? Pacientes já vinculados mantêm o vínculo.`)) return;
    try {
      await axios.delete(getApiUrl(`/clinic/patient-sources/${r.id}`));
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erro ao desativar');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Origens de pacientes</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Lista usada ao cadastrar ou editar pacientes para indicar como conheceram a clínica (marketing).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488', textTransform: 'none' } }}>
          Nova origem
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 12px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f0fdf4' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Ordem</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#0d9488' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhuma origem cadastrada</TableCell></TableRow>
              ) : (
                rows.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)).map((r) => (
                  <TableRow key={r.id} hover sx={{ opacity: r.is_active ? 1 : 0.85 }}>
                    <TableCell>{r.sort_order}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell>
                      {r.is_active ? (
                        <Chip label="Ativo" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                      ) : (
                        <Chip label="Inativo" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#0d9488' }}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      {r.is_active && (
                        <Tooltip title="Desativar">
                          <IconButton size="small" onClick={() => softDelete(r)} sx={{ color: '#94a3b8' }}>
                            <VisibilityOffOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dlgOpen} onClose={() => !saving && setDlgOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>{editId ? 'Editar origem' : 'Nova origem'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} margin="dense" />
          <TextField fullWidth label="Ordem de exibição" type="number" value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} margin="dense" sx={{ mt: 1 }}
            helperText="Menor aparece primeiro na lista de seleção" />
          <FormControlLabel sx={{ mt: 1 }}
            control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} color="success" />}
            label={form.is_active ? 'Ativa' : 'Inativa'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !form.name.trim()} onClick={() => save()} sx={{ bgcolor: '#14b8a6' }}>
            {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientSources;
